import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCreateOrganization } from '../hooks/useCreateOrganization'
import { useTenants } from '@/features/tenants/hooks/useTenants'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const createOrganizationSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be 50 characters or less')
    .regex(/^[a-z0-9_-]+$/, 'Name can only contain lowercase letters, numbers, hyphens (-), and underscores (_)')
    .refine((val) => !val.startsWith('-') && !val.endsWith('-'), {
      message: 'Name cannot start or end with a hyphen',
    }),
  displayName: z
    .string()
    .min(1, 'Display name is required')
    .max(100, 'Display name must be 100 characters or less')
    .refine((val) => val.trim().length > 0, {
      message: 'Display name cannot be only whitespace',
    }),
  domain: z
    .string()
    .min(1, 'Domain is required')
    .max(253, 'Domain must be 253 characters or less')
    .refine((val) => {
      // Valid domain format: letters, numbers, hyphens, dots
      // Must have at least one dot, no consecutive dots, no leading/trailing dots or hyphens
      const domainRegex = /^(?!-)[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/
      return domainRegex.test(val)
    }, {
      message: 'Invalid domain format (e.g., example.com)',
    }),
  isActive: z.boolean().default(true),
  tenantId: z.string().optional(),
})

type CreateOrganizationForm = z.infer<typeof createOrganizationSchema>

interface CreateOrganizationDialogProps {
  open: boolean
  onClose: () => void
}

export function CreateOrganizationDialog({ open, onClose }: CreateOrganizationDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { mutate: createOrganization } = useCreateOrganization()
  const { data: tenantsData, isLoading: isLoadingTenants } = useTenants(1, 100)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm<CreateOrganizationForm>({
    resolver: zodResolver(createOrganizationSchema),
    defaultValues: {
      name: '',
      displayName: '',
      domain: '',
      isActive: true,
      tenantId: '',
    },
  })

  const isActive = watch('isActive')
  const tenantId = watch('tenantId')
  const tenants = tenantsData?.tenants || []

  const onSubmit = async (data: CreateOrganizationForm) => {
    setIsSubmitting(true)

    try {
      const submitData = {
        name: data.name,
        displayName: data.displayName,
        domain: data.domain,
        isActive: data.isActive,
        tenantId: data.tenantId || undefined,
      }

      createOrganization(submitData, {
        onSuccess: () => {
          reset()
          onClose()
        },
        onSettled: () => {
          setIsSubmitting(false)
        }
      })
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      reset()
      onClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Organization</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              placeholder="e.g., example-org"
              {...register('name')}
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Lowercase letters, numbers, hyphens (-), and underscores (_) only
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name *</Label>
            <Input
              id="displayName"
              placeholder="e.g., Example Organization"
              {...register('displayName')}
              disabled={isSubmitting}
            />
            {errors.displayName && (
              <p className="text-sm text-destructive">{errors.displayName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="domain">Domain *</Label>
            <Input
              id="domain"
              placeholder="e.g., example.com"
              {...register('domain')}
              disabled={isSubmitting}
            />
            {errors.domain && (
              <p className="text-sm text-destructive">{errors.domain.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Must be unique across all organizations
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tenantId">Tenant</Label>
            <Select
              value={tenantId || 'none'}
              onValueChange={(value) => setValue('tenantId', value === 'none' ? '' : value)}
              disabled={isSubmitting || isLoadingTenants}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a Tenant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">(default Tenant)</SelectItem>
                {tenants.map((tenant) => (
                  <SelectItem key={tenant._id} value={tenant._id}>
                    {tenant.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Assign this organization to a tenant
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={(checked) => setValue('isActive', checked)}
              disabled={isSubmitting}
            />
            <Label htmlFor="isActive">Active</Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Organization'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
